import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flag, Users, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ClaimChallengeFlowProps {
  challengeId: string;
  onClaimed: () => void;
}

const ClaimChallengeFlow: React.FC<ClaimChallengeFlowProps> = ({
  challengeId,
  onClaimed,
}) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [teamEmails, setTeamEmails] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState<string | null>(null);

  const checkIfBlocked = async (userId: string): Promise<boolean> => {
    // Check if user is blocked from this specific challenge or globally
    const { data: blocks } = await supabase
      .from("challenge_claim_blocks")
      .select("reason, challenge_id")
      .eq("user_id", userId)
      .or(`challenge_id.eq.${challengeId},challenge_id.is.null`);

    if (blocks && blocks.length > 0) {
      const globalBlock = blocks.find((b) => b.challenge_id === null);
      const specificBlock = blocks.find((b) => b.challenge_id === challengeId);
      
      setIsBlocked(true);
      setBlockReason(
        globalBlock?.reason || specificBlock?.reason || "You are not allowed to claim challenges."
      );
      return true;
    }
    return false;
  };

  const handleClaimClick = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      navigate(`/login?redirect=/challenges?claim=${challengeId}`);
      return;
    }

    // Check if user is blocked
    const blocked = await checkIfBlocked(user.id);
    if (blocked) {
      setDialogOpen(true);
      return;
    }

    setIsBlocked(false);
    setBlockReason(null);
    setDialogOpen(true);
  };

  const claimChallenge = async () => {
    setClaiming(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Double-check block status
      const blocked = await checkIfBlocked(user.id);
      if (blocked) {
        toast.error("You are not allowed to claim this challenge");
        return;
      }

      // Update challenge with claimed_by (no mandatory team creation)
      const { error: challengeError } = await supabase
        .from("challenges")
        .update({
          status: "in_progress",
          claimed_at: new Date().toISOString(),
          claimed_by: user.id,
        })
        .eq("id", challengeId);

      if (challengeError) throw challengeError;

      // Only create team and send invites if emails are provided
      if (teamEmails.trim()) {
        // Add user as team lead (creates the team membership)
        const { error: memberError } = await supabase
          .from("challenge_team_members")
          .insert({
            challenge_id: challengeId,
            user_id: user.id,
            role: "lead",
          });

        if (memberError) {
          console.error("Failed to create team lead membership:", memberError);
        } else {
          // Get the team ID from the membership we just created
          const { data: membershipData } = await supabase
            .from("challenge_team_members")
            .select("team_id")
            .eq("user_id", user.id)
            .eq("challenge_id", challengeId)
            .single();

          if (membershipData?.team_id) {
            const emails = teamEmails.split(",").map((e) => e.trim()).filter(Boolean);
            let sentCount = 0;

            for (const email of emails) {
              try {
                const { data: invitation } = await supabase
                  .from("team_member_invitations")
                  .insert({
                    team_id: membershipData.team_id,
                    challenge_id: challengeId,
                    invited_by: user.id,
                    invitee_name: email.split("@")[0],
                    invitee_email: email,
                    invitation_type: "join_existing",
                  })
                  .select()
                  .single();

                if (invitation) {
                  await supabase.functions.invoke("send-team-invite", {
                    body: { invitation_id: invitation.id },
                  });
                  sentCount++;
                }
              } catch (err) {
                console.error("Failed to send invite to", email, err);
              }
            }

            toast.success(`Challenge claimed! ${sentCount} invite(s) sent.`);
          }
        }
      } else {
        toast.success("Challenge claimed! You are now the owner.");
      }

      setDialogOpen(false);
      onClaimed();
    } catch (error: any) {
      console.error("Error claiming challenge:", error);
      toast.error(error.message || "Failed to claim challenge");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <>
      <Button onClick={handleClaimClick} className="gap-2">
        <Flag className="h-4 w-4" />
        Claim Challenge
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isBlocked ? "Cannot Claim Challenge" : "Claim This Challenge"}
            </DialogTitle>
            <DialogDescription>
              {isBlocked
                ? "You are currently not allowed to claim this challenge."
                : "You'll become the owner of this challenge. You can optionally invite team members now or later."}
            </DialogDescription>
          </DialogHeader>

          {isBlocked ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {blockReason || "You have been blocked from claiming challenges."}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="team-emails" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Invite Team Members (optional)
                </Label>
                <Input
                  id="team-emails"
                  placeholder="email1@example.com, email2@example.com"
                  value={teamEmails}
                  onChange={(e) => setTeamEmails(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Enter email addresses separated by commas. They'll be able to join your team.
                  You can also claim the challenge without a team.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {isBlocked ? "Close" : "Cancel"}
            </Button>
            {!isBlocked && (
              <Button onClick={claimChallenge} disabled={claiming}>
                {claiming ? "Claiming..." : "Claim Challenge"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClaimChallengeFlow;
